'use client'

/**
 * Navigation Context — single source of truth for intent-driven navigation,
 * guidance mode, and banner visibility state.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import {
  MacroIntent,
  DiscoverMode,
  DashboardMode,
  PortfolioMode,
  TradingFocus,
  INTENT_CARDS,
} from '@/types/navigation.types'

interface NavigationContextState {
  // Mode states
  discoverMode: DiscoverMode
  dashboardMode: DashboardMode
  portfolioMode: PortfolioMode
  tradingFocus: TradingFocus

  setDiscoverMode: (mode: DiscoverMode) => void
  setDashboardMode: (mode: DashboardMode) => void
  setPortfolioMode: (mode: PortfolioMode) => void
  setTradingFocus: (focus: TradingFocus) => void

  resetAllModes: () => void

  // Intent
  currentIntent: MacroIntent | null
  setCurrentIntent: (intent: MacroIntent | null) => void

  // Intent menu
  intentMenuVisible: boolean
  openIntentMenu: () => void
  closeIntentMenu: () => void

  // Onboarding
  hasCompletedOnboarding: boolean
  completeOnboarding: () => void
  resetOnboarding: () => void

  // Banner dismissal
  bannerDismissed: boolean
  dismissBanner: () => void
  showBanner: () => void

  // Guidance mode — controls whether orientation banner is available at all.
  // On by default for users in first 7 days; persisted to localStorage.
  guidanceModeEnabled: boolean
  setGuidanceModeEnabled: (enabled: boolean) => void
  toggleGuidanceMode: () => void
  resetGuidance: () => void

  // Derived: banner is visible only when guidance is on AND not dismissed
  isGuidanceVisible: boolean

  activateIntent: (intentId: MacroIntent) => string | null
}

const NavigationContext = createContext<NavigationContextState | undefined>(undefined)

const STORAGE_KEY_ONBOARDING    = 'supremepm_onboarding_complete'
const STORAGE_KEY_BANNER        = 'supremepm_banner_dismissed'
const STORAGE_KEY_INTENT        = 'supremepm_current_intent'
const STORAGE_KEY_GUIDANCE_MODE = 'supremepm_guidance_mode'
const STORAGE_KEY_FIRST_SEEN_AT = 'supremepm_first_seen_at'

export function NavigationContextProvider({ children }: { children: ReactNode }) {
  const [discoverMode, setDiscoverMode]   = useState<DiscoverMode>(null)
  const [dashboardMode, setDashboardMode] = useState<DashboardMode>(null)
  const [portfolioMode, setPortfolioMode] = useState<PortfolioMode>(null)
  const [tradingFocus, setTradingFocus]   = useState<TradingFocus>(null)
  const [intentMenuVisible, setIntentMenuVisible] = useState(false)
  const [currentIntent, setCurrentIntentState]    = useState<MacroIntent | null>(null)
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(true)
  const [bannerDismissed, setBannerDismissed]               = useState(false)
  const [guidanceModeEnabled, setGuidanceModeEnabledState]  = useState(true)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const onboardingComplete = localStorage.getItem(STORAGE_KEY_ONBOARDING)
    setHasCompletedOnboarding(onboardingComplete === 'true')

    const savedIntent = localStorage.getItem(STORAGE_KEY_INTENT)
    if (savedIntent) setCurrentIntentState(savedIntent as MacroIntent)

    const dismissed = localStorage.getItem(STORAGE_KEY_BANNER) === 'true'
    setBannerDismissed(dismissed)

    const now = Date.now()
    const storedFirstSeenAt = localStorage.getItem(STORAGE_KEY_FIRST_SEEN_AT)
    const resolvedFirstSeenAt = storedFirstSeenAt ?? String(now)

    if (!storedFirstSeenAt) {
      localStorage.setItem(STORAGE_KEY_FIRST_SEEN_AT, resolvedFirstSeenAt)
    }

    const storedGuidance = localStorage.getItem(STORAGE_KEY_GUIDANCE_MODE)
    if (storedGuidance) {
      setGuidanceModeEnabledState(storedGuidance === 'true')
      return
    }

    // First time seeing the guidance key: enable for users within first 7 days
    const seenAt = Number(resolvedFirstSeenAt)
    const ageDays = (now - seenAt) / (1000 * 60 * 60 * 24)
    const defaultEnabled = ageDays <= 7
    setGuidanceModeEnabledState(defaultEnabled)
    localStorage.setItem(STORAGE_KEY_GUIDANCE_MODE, String(defaultEnabled))
  }, [])

  const resetAllModes = () => {
    setDiscoverMode(null)
    setDashboardMode(null)
    setPortfolioMode(null)
    setTradingFocus(null)
  }

  const openIntentMenu  = () => setIntentMenuVisible(true)
  const closeIntentMenu = () => setIntentMenuVisible(false)

  const setCurrentIntent = (intent: MacroIntent | null) => {
    setCurrentIntentState(intent)
    if (typeof window === 'undefined') return
    if (intent) {
      localStorage.setItem(STORAGE_KEY_INTENT, intent)
    } else {
      localStorage.removeItem(STORAGE_KEY_INTENT)
    }
  }

  const completeOnboarding = () => {
    setHasCompletedOnboarding(true)
    localStorage.setItem(STORAGE_KEY_ONBOARDING, 'true')
  }

  const resetOnboarding = () => {
    setHasCompletedOnboarding(false)
    localStorage.removeItem(STORAGE_KEY_ONBOARDING)
  }

  const setGuidanceModeEnabled = (enabled: boolean) => {
    setGuidanceModeEnabledState(enabled)
    localStorage.setItem(STORAGE_KEY_GUIDANCE_MODE, String(enabled))
    // Re-enabling guidance also clears the per-session banner dismissal
    if (enabled) {
      setBannerDismissed(false)
      localStorage.removeItem(STORAGE_KEY_BANNER)
    }
  }

  const dismissBanner = () => {
    setBannerDismissed(true)
    localStorage.setItem(STORAGE_KEY_BANNER, 'true')
  }

  const showBanner = () => {
    setGuidanceModeEnabled(true)
    setBannerDismissed(false)
    localStorage.removeItem(STORAGE_KEY_BANNER)
  }

  const toggleGuidanceMode = () => setGuidanceModeEnabled(!guidanceModeEnabled)

  const resetGuidance = () => {
    setGuidanceModeEnabledState(true)
    setBannerDismissed(false)
    localStorage.removeItem(STORAGE_KEY_BANNER)
    localStorage.setItem(STORAGE_KEY_GUIDANCE_MODE, 'true')
  }

  const activateIntent = (intentId: MacroIntent): string | null => {
    const intent = INTENT_CARDS.find(c => c.id === intentId)
    if (!intent) return null

    setCurrentIntent(intentId)

    if (intent.targetState.discoverMode !== undefined) setDiscoverMode(intent.targetState.discoverMode)
    if (intent.targetState.dashboardMode !== undefined) setDashboardMode(intent.targetState.dashboardMode)
    if (intent.targetState.portfolioMode !== undefined) setPortfolioMode(intent.targetState.portfolioMode)
    if (intent.targetState.tradingFocus !== undefined) setTradingFocus(intent.targetState.tradingFocus)

    closeIntentMenu()
    return intent.targetRoute
  }

  const isGuidanceVisible = guidanceModeEnabled && !bannerDismissed

  return (
    <NavigationContext.Provider
      value={{
        discoverMode,
        dashboardMode,
        portfolioMode,
        tradingFocus,
        setDiscoverMode,
        setDashboardMode,
        setPortfolioMode,
        setTradingFocus,
        resetAllModes,
        currentIntent,
        setCurrentIntent,
        intentMenuVisible,
        openIntentMenu,
        closeIntentMenu,
        hasCompletedOnboarding,
        completeOnboarding,
        resetOnboarding,
        bannerDismissed,
        dismissBanner,
        showBanner,
        guidanceModeEnabled,
        setGuidanceModeEnabled,
        toggleGuidanceMode,
        resetGuidance,
        isGuidanceVisible,
        activateIntent,
      }}
    >
      {children}
    </NavigationContext.Provider>
  )
}

export function useNavigationContext() {
  const context = useContext(NavigationContext)
  if (!context) {
    throw new Error('useNavigationContext must be used within NavigationContextProvider')
  }
  return context
}
